import { useState } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Phone, ShieldCheck, Award, Clock, FileCheck, CheckCircle2, MapPin, Star, ArrowRight,
  Paintbrush, Home as HomeIcon, PaintRoller, PaintBucket, Fence, Building2, DoorOpen, Menu, X,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
export const LOGO = "https://customer-assets.emergentagent.com/job_schedule-pro-262/artifacts/ef9s6s9a_image0.jpeg";
export const PHONE = "416-830-6872";
export const EMAIL = "segalaurorapainters@gmail.com";
export const PHONE_TEL = "4168306872";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08 } }),
};

export const services = [
  { icon: PaintRoller, title: "Interior Painting", desc: "Walls, ceilings & rooms refreshed with a flawless finish." },
  { icon: HomeIcon, title: "Exterior Painting", desc: "Durable, weather-resistant coatings that boost curb appeal." },
  { icon: PaintBucket, title: "Ceiling Painting", desc: "Smooth, even ceilings with clean cut-in lines." },
  { icon: DoorOpen, title: "Trim & Baseboards", desc: "Crisp detailing on doors, trim and baseboards." },
  { icon: Paintbrush, title: "Drywall Repairs", desc: "Patch, sand and paint for a seamless surface." },
  { icon: Fence, title: "Deck & Fence Staining", desc: "Protect and beautify outdoor wood surfaces." },
  { icon: Building2, title: "Commercial Painting", desc: "Reliable painting for offices and commercial spaces." },
];

const badges = [
  { icon: ShieldCheck, label: "Fully Insured" },
  { icon: Award, label: "Quality Workmanship" },
  { icon: Clock, label: "On Time & Reliable" },
  { icon: FileCheck, label: "Free Estimates" },
];

const whyUs = [
  "Experienced & Professional Painters",
  "Quality Materials & Lasting Results",
  "Clean, Careful & Respectful",
  "Competitive Pricing",
  "100% Satisfaction Guaranteed",
  "Fully Insured for Your Peace of Mind",
];

const areas = ["Aurora", "Newmarket", "Richmond Hill", "Vaughan", "King City", "Markham", "& Surrounding GTA Areas"];

const reviews = [
  { name: "Aurora Homeowner", text: "Amazing work from start to finish! Professional, punctual and the results are outstanding. Highly recommend Segal Aurora Painters!" },
  { name: "Sarah M., Newmarket", text: "They repainted our entire main floor and it looks brand new. Clean, tidy and respectful of our home the whole time." },
  { name: "David R., Richmond Hill", text: "Great communication and fair pricing. The exterior of our house has never looked better. Will definitely use them again." },
  { name: "Jennifer L., Vaughan", text: "On time, on budget, and the quality is excellent. The team was friendly and did a fantastic job on our deck staining." },
  { name: "Michael T., King City", text: "Fully insured and truly professional. They transformed our commercial office space over a weekend with zero hassle." },
  { name: "Lisa K., Markham", text: "From the free estimate to the final walkthrough, everything was smooth. Beautiful, lasting results. 5 stars!" },
];

const areaDetails = [
  { name: "Aurora", note: "Our home base — fast, local service." },
  { name: "Newmarket", note: "Residential & commercial painting." },
  { name: "Richmond Hill", note: "Interior & exterior specialists." },
  { name: "Vaughan", note: "Trusted by homeowners across the city." },
  { name: "King City", note: "Premium finishes for every home." },
  { name: "Markham", note: "Reliable, insured painting crews." },
];

export const gallery = [
  { url: "https://images.unsplash.com/photo-1633330977020-2bdfb8530cc2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", tag: "Interior Living Room" },
  { url: "https://images.pexels.com/photos/23417421/pexels-photo-23417421.jpeg?auto=compress&cs=tinysrgb&w=1200", tag: "Exterior Facade" },
  { url: "https://images.pexels.com/photos/221027/pexels-photo-221027.jpeg?auto=compress&cs=tinysrgb&w=1200", tag: "Fence Staining" },
  { url: "https://images.unsplash.com/photo-1632829882891-5047ccc421bc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", tag: "Bright Interiors" },
];

const SERVICE_OPTIONS = services.map((s) => s.title).concat("Other / Not Sure");

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/gallery", label: "Gallery" },
  { to: "/contact", label: "Contact" },
];

export function SectionTitle({ overline, title, align = "center" }) {
  return (
    <div className={align === "center" ? "text-center" : "text-left"}>
      <span className="inline-block text-brand-orange font-head font-bold text-sm tracking-[0.25em] uppercase">{overline}</span>
      <h2 className={`mt-3 font-head font-black text-navy text-3xl sm:text-4xl tracking-tight ${align === "center" ? "mx-auto" : ""}`}>
        {title}
      </h2>
      <div className={`mt-4 h-1 w-16 bg-brand-orange ${align === "center" ? "mx-auto" : ""}`} />
    </div>
  );
}

export function Header() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  return (
    <header className="sticky top-0 z-50 bg-navy border-b border-white/10" data-testid="site-header">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-28 lg:h-32 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="logo-link">
          <img src={LOGO} alt="Segal Aurora Painters" className="h-24 lg:h-28 w-auto object-contain" data-testid="logo-image" />
        </Link>

        <nav className="hidden lg:flex items-center gap-8" data-testid="main-nav">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={`font-head font-bold text-sm tracking-wide uppercase transition-colors ${pathname === n.to ? "text-brand-orange" : "text-white hover:text-brand-orange"}`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a href={`tel:${PHONE_TEL}`} className="hidden md:flex items-center gap-2 font-head font-extrabold text-white text-base lg:text-lg" data-testid="header-phone">
            <Phone className="w-5 h-5 text-brand-orange" /> {PHONE}
          </a>
          <Link to="/contact" data-testid="header-cta">
            <Button className="rounded-none bg-brand-orange hover:bg-orange-600 font-head font-bold text-white h-11 px-5 text-sm sm:text-base">
              Free Estimate
            </Button>
          </Link>
          <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="lg:hidden bg-navy border-t border-white/10 px-4 pb-4" data-testid="mobile-nav">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              onClick={() => setOpen(false)}
              className={`block py-3 font-head font-bold uppercase text-sm border-b border-white/5 ${pathname === n.to ? "text-brand-orange" : "text-white"}`}
            >
              {n.label}
            </Link>
          ))}
        </nav>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer className="bg-navy-deep text-slate-300 py-16" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-3 gap-10">
          <div>
            <img src={LOGO} alt="Segal Aurora Painters" className="h-20 w-auto object-contain" data-testid="footer-logo" />
            <p className="mt-4 text-sm max-w-xs leading-relaxed">
              Quality painting. Beautiful results. Residential & commercial painting you can trust.
            </p>
          </div>
          <div>
            <h4 className="font-head font-bold text-white mb-4">Quick Links</h4>
            <ul className="space-y-2 text-sm">
              {NAV.map((n) => (
                <li key={n.to}><Link to={n.to} className="hover:text-brand-orange">{n.label}</Link></li>
              ))}
              <li><Link to="/reviews" className="hover:text-brand-orange">Reviews</Link></li>
              <li><Link to="/areas" className="hover:text-brand-orange">Service Areas</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-head font-bold text-white mb-4">Contact</h4>
            <a href={`tel:${PHONE_TEL}`} className="font-head font-black text-2xl text-brand-orange block" data-testid="footer-phone">{PHONE}</a>
            <a href={`mailto:${EMAIL}`} className="mt-3 text-sm text-slate-200 hover:text-brand-orange block break-all" data-testid="footer-email">{EMAIL}</a>
            <p className="mt-3 text-sm">14 Wellington St E, Unit 3, Aurora, ON</p>
            <p className="mt-2 text-sm">Serving Aurora, Newmarket, Richmond Hill, Vaughan, King City, Markham & the GTA.</p>
            <p className="mt-2 text-sm">Open Mon–Sun · 9:00 AM – 9:00 PM</p>
            <Link to="/contact">
              <Button className="mt-5 rounded-none bg-brand-orange hover:bg-orange-600 text-white font-head font-bold" data-testid="footer-cta">
                Get a Free Estimate
              </Button>
            </Link>
          </div>
        </div>
        <div className="mt-12 pt-6 border-t border-white/10 text-xs text-slate-500 flex flex-col sm:flex-row justify-between gap-2">
          <span>© 2026 Segal Aurora Painters. All rights reserved.</span>
          <a href={`mailto:${EMAIL}`} className="text-slate-300 hover:text-brand-orange break-all" data-testid="footer-bottom-email">{EMAIL}</a>
          <span>Fully Insured · Free Estimates · Quality Workmanship</span>
        </div>
      </div>
    </footer>
  );
}

export function PageHero({ overline, title, subtitle }) {
  return (
    <section className="relative bg-navy overflow-hidden" data-testid="page-hero">
      <div className="absolute inset-0 grain opacity-[0.15] pointer-events-none" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 md:py-24 text-center">
        <span className="inline-block text-brand-orange font-head font-bold text-sm tracking-[0.25em] uppercase">{overline}</span>
        <h1 className="mt-3 font-head font-black text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight">{title}</h1>
        {subtitle && <p className="mt-5 text-slate-300 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">{subtitle}</p>}
        <div className="mt-4 h-1 w-16 bg-brand-orange mx-auto" />
      </div>
    </section>
  );
}

export function TrustBadges() {
  return (
    <section className="bg-slate-50 border-b border-slate-200" data-testid="trust-badges">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4">
        {badges.map((b) => (
          <div key={b.label} className="flex items-center gap-3 py-6 px-2 lg:px-6 border-b lg:border-b-0 lg:border-r border-slate-200 last:border-0">
            <b.icon className="w-8 h-8 text-brand-orange shrink-0" />
            <span className="font-head font-bold text-navy text-sm sm:text-base leading-tight">{b.label}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

export function AboutBlock() {
  return (
    <section className="py-20 md:py-28" data-testid="about-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <div className="relative">
          <div className="border-4 border-navy">
            <img
              src="https://images.unsplash.com/photo-1688372199140-cade7ae820fe?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
              alt="Segal Aurora Painters at work"
              className="w-full h-[360px] object-cover"
              data-testid="about-image"
            />
          </div>
          <div className="absolute -bottom-5 -right-5 bg-brand-orange text-white p-5 hidden sm:block">
            <p className="font-head font-black text-3xl leading-none">100%</p>
            <p className="text-sm mt-1 font-medium max-w-[160px]">Satisfaction guaranteed on every project</p>
          </div>
        </div>
        <div>
          <SectionTitle overline="About Us" title="Aurora's Trusted Painting Team" align="left" />
          <p className="mt-6 text-slate-600 leading-relaxed">
            Based in Aurora, Ontario, <strong className="text-navy">Segal Aurora Painters</strong> is a trusted team of
            professional painters proudly serving Aurora and the Greater Toronto Area. With over 12 years of hands-on
            experience, we specialize in interior and exterior painting, drywall repairs, and deck &amp; fence staining
            for both residential and commercial properties.
          </p>
          <p className="mt-4 text-slate-600 leading-relaxed">
            We're fully insured and committed to clean, careful, and respectful service — delivering lasting results
            and 100% satisfaction on every job. From a single room refresh to a complete exterior transformation, we
            bring your vision to life with quality materials and expert craftsmanship.
          </p>
          <div className="mt-8 grid grid-cols-2 gap-4">
            <div className="border-l-4 border-brand-orange pl-4">
              <p className="font-head font-black text-3xl text-navy">12+</p>
              <p className="text-sm text-slate-500">Years Experience</p>
            </div>
            <div className="border-l-4 border-brand-orange pl-4">
              <p className="font-head font-black text-3xl text-navy">5.0★</p>
              <p className="text-sm text-slate-500">Google Rating</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function GalleryBlock() {
  return (
    <section className="py-20 md:py-28" data-testid="gallery">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionTitle overline="Our Work" title="Results That Speak for Themselves" />
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {gallery.map((g, i) => (
            <motion.div key={g.tag} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
              className="group relative overflow-hidden border border-slate-200">
              <img src={g.url} alt={g.tag} className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <span className="absolute bottom-4 left-4 text-white font-head font-bold text-sm opacity-0 group-hover:opacity-100 transition-opacity">{g.tag}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function ServicesBlock() {
  return (
    <section className="py-20 md:py-28 bg-slate-50" data-testid="services">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionTitle overline="What We Do" title="Our Painting Services" />
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {services.map((s, i) => (
            <motion.div key={s.title} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
              className="bg-white border border-slate-200 p-8 transition-all hover:-translate-y-1 hover:shadow-lg hover:border-orange-300"
              data-testid={`service-card-${i}`}>
              <div className="w-12 h-12 bg-navy flex items-center justify-center mb-5">
                <s.icon className="w-6 h-6 text-brand-orange" />
              </div>
              <h3 className="font-head font-bold text-navy text-xl">{s.title}</h3>
              <p className="mt-2 text-slate-500 text-sm leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function WhyUsBlock() {
  return (
    <section className="py-20 md:py-28" data-testid="why-us">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-8">
        <div className="bg-navy p-8 sm:p-10 text-white">
          <h3 className="font-head font-black text-2xl sm:text-3xl">Why Choose Us?</h3>
          <ul className="mt-6 space-y-4">
            {whyUs.map((w) => (
              <li key={w} className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-brand-orange shrink-0 mt-0.5" />
                <span className="text-slate-200 text-sm sm:text-base">{w}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="border border-slate-200 p-8 sm:p-10">
          <h3 className="font-head font-black text-2xl text-navy">Serving Aurora & The GTA</h3>
          <ul className="mt-6 space-y-3">
            {areas.map((a) => (
              <li key={a} className="flex items-center gap-3 text-slate-700">
                <MapPin className="w-4 h-4 text-brand-orange shrink-0" />
                <span className="text-sm sm:text-base">{a}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="border border-slate-200 p-8 sm:p-10 flex flex-col">
          <span className="font-head font-black text-2xl text-navy">Google Reviews</span>
          <div className="mt-4 flex items-center gap-3">
            <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} className="w-6 h-6 fill-orange-400 text-orange-400" />)}</div>
            <span className="font-head font-black text-3xl text-navy">5.0</span>
          </div>
          <p className="mt-2 text-sm text-slate-500">Based on 100+ reviews</p>
          <blockquote className="mt-6 text-slate-700 italic leading-relaxed border-l-4 border-brand-orange pl-4">
            "Amazing work from start to finish! Professional, punctual and the results are outstanding.
            Highly recommend Segal Aurora Painters!"
          </blockquote>
          <p className="mt-4 font-head font-bold text-brand-orange text-sm">— Aurora Homeowner</p>
        </div>
      </div>
    </section>
  );
}

export function LocationBlock() {
  return (
    <section className="py-20 md:py-28" data-testid="location-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionTitle overline="Visit Us" title="Find Us in Aurora" />
        <div className="mt-12 grid lg:grid-cols-2 gap-8 items-stretch">
          <div className="bg-navy p-8 sm:p-10 text-white flex flex-col justify-center">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-brand-orange flex items-center justify-center shrink-0"><MapPin className="w-6 h-6 text-white" /></div>
              <div>
                <p className="font-head font-bold text-sm tracking-widest uppercase text-orange-400">Our Address</p>
                <p className="mt-1 font-head font-black text-2xl leading-snug" data-testid="business-address">14 Wellington St E, Unit 3<br />Aurora, ON</p>
              </div>
            </div>
            <div className="mt-8 flex items-start gap-4">
              <div className="w-12 h-12 bg-white/10 flex items-center justify-center shrink-0"><Phone className="w-6 h-6 text-brand-orange" /></div>
              <div>
                <p className="font-head font-bold text-sm tracking-widest uppercase text-orange-400">Call or Text</p>
                <a href={`tel:${PHONE_TEL}`} className="mt-1 font-head font-black text-2xl text-white block" data-testid="location-phone">{PHONE}</a>
              </div>
            </div>
            <div className="mt-8 flex items-start gap-4">
              <div className="w-12 h-12 bg-white/10 flex items-center justify-center shrink-0"><Clock className="w-6 h-6 text-brand-orange" /></div>
              <div>
                <p className="font-head font-bold text-sm tracking-widest uppercase text-orange-400">Business Hours</p>
                <p className="mt-1 font-head font-bold text-lg text-white" data-testid="business-hours">Monday – Sunday</p>
                <p className="text-slate-200">9:00 AM – 9:00 PM</p>
              </div>
            </div>
            <a href="https://www.google.com/maps/search/?api=1&query=14+Wellington+St+E+Unit+3+Aurora+ON" target="_blank" rel="noopener noreferrer" data-testid="directions-link">
              <Button className="mt-8 rounded-none bg-brand-orange hover:bg-orange-600 text-white font-head font-bold h-12 px-6">Get Directions <ArrowRight className="w-4 h-4 ml-1" /></Button>
            </a>
          </div>
          <div className="border border-slate-200 min-h-[360px]">
            <iframe title="Segal Aurora Painters location" src="https://www.google.com/maps?q=14+Wellington+St+E+Unit+3+Aurora+ON&output=embed"
              className="w-full h-full min-h-[360px]" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-testid="location-map" />
          </div>
        </div>
      </div>
    </section>
  );
}

export function EstimateForm() {
  const [form, setForm] = useState({ full_name: "", phone: "", email: "", service: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.email || !form.service) {
      toast.error("Please fill in your name, phone, email and service needed.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/estimates`, form);
      toast.success("Thanks! We'll contact you shortly for your free estimate.");
      setForm({ full_name: "", phone: "", email: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please call us at " + PHONE);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-20 md:py-28 bg-slate-50" data-testid="estimate-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <SectionTitle overline="No Obligation" title="Get Your Free Estimate" align="left" />
          <p className="mt-5 text-slate-600 max-w-md leading-relaxed">
            Fast response, free estimates and no obligation. Tell us about your project and we'll get right back to you.
          </p>
          <div className="mt-8 flex items-center gap-4">
            <div className="w-14 h-14 bg-navy flex items-center justify-center"><Phone className="w-6 h-6 text-brand-orange" /></div>
            <div>
              <p className="text-sm text-slate-500 font-head font-semibold">Call or Text Us Today!</p>
              <a href={`tel:${PHONE_TEL}`} className="font-head font-black text-3xl text-brand-orange" data-testid="form-phone">{PHONE}</a>
            </div>
          </div>
          <div className="mt-4">
            <a href={`mailto:${EMAIL}`} className="text-slate-600 hover:text-brand-orange break-all" data-testid="form-email">{EMAIL}</a>
          </div>
        </div>

        <form onSubmit={submit} className="bg-navy p-6 sm:p-10" data-testid="estimate-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Input data-testid="input-name" placeholder="Full Name *" value={form.full_name} onChange={(e) => update("full_name", e.target.value)}
              className="rounded-none bg-white/10 border-white/40 text-white placeholder:text-white/80 h-12 focus-visible:ring-orange-500" />
            <Input data-testid="input-phone" placeholder="Phone Number *" value={form.phone} onChange={(e) => update("phone", e.target.value)}
              className="rounded-none bg-white/10 border-white/40 text-white placeholder:text-white/80 h-12 focus-visible:ring-orange-500" />
          </div>
          <Input data-testid="input-email" type="email" placeholder="Email Address *" value={form.email} onChange={(e) => update("email", e.target.value)}
            className="mt-4 rounded-none bg-white/10 border-white/40 text-white placeholder:text-white/80 h-12 focus-visible:ring-orange-500" />
          <Select value={form.service} onValueChange={(v) => update("service", v)}>
            <SelectTrigger data-testid="select-service" className="mt-4 rounded-none bg-white/10 border-white/40 text-white h-12 data-[placeholder]:text-white/80 focus:ring-orange-500">
              <SelectValue placeholder="Service Needed *" />
            </SelectTrigger>
            <SelectContent>
              {SERVICE_OPTIONS.map((opt) => (<SelectItem key={opt} value={opt} data-testid={`service-option-${opt}`}>{opt}</SelectItem>))}
            </SelectContent>
          </Select>
          <Textarea data-testid="input-message" placeholder="Message (optional)" value={form.message} onChange={(e) => update("message", e.target.value)} rows={4}
            className="mt-4 rounded-none bg-white/10 border-white/40 text-white placeholder:text-white/80 focus-visible:ring-orange-500 resize-none" />
          <Button type="submit" disabled={submitting} data-testid="submit-estimate"
            className="mt-5 w-full rounded-none bg-brand-orange hover:bg-orange-600 text-white font-head font-black h-14 text-base tracking-wide">
            {submitting ? "Sending..." : "GET MY FREE ESTIMATE"}
          </Button>
        </form>
      </div>
    </section>
  );
}

export function HomeHero() {
  return (
    <section className="relative bg-navy overflow-hidden" data-testid="hero">
      <div className="absolute inset-0 grain opacity-[0.15] pointer-events-none" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center py-16 lg:py-24">
        <motion.div initial="hidden" animate="show" variants={fadeUp}>
          <div className="inline-flex items-center gap-2 border border-orange-500/40 text-orange-400 px-3 py-1 mb-6 text-xs font-head font-bold tracking-widest uppercase">
            <span className="w-1.5 h-1.5 bg-brand-orange" /> Proudly Serving Aurora & The GTA
          </div>
          <h1 className="font-head font-black text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            Professional <span className="text-brand-orange">Interior & Exterior</span> Painting
          </h1>
          <p className="mt-5 text-slate-300 text-base sm:text-lg max-w-xl leading-relaxed">
            Transform your home with lasting, high-quality workmanship. Fully insured, on time, and 100% satisfaction guaranteed. Let's bring your vision to life.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <Link to="/contact" data-testid="hero-cta">
              <Button className="w-full sm:w-auto rounded-none bg-brand-orange hover:bg-orange-600 text-white font-head font-bold h-14 px-8 text-base">
                Get Your Free Estimate <ArrowRight className="w-5 h-5 ml-1" />
              </Button>
            </Link>
            <a href={`tel:${PHONE_TEL}`} data-testid="hero-call">
              <Button variant="outline" className="w-full sm:w-auto rounded-none border-2 border-white/30 bg-transparent text-white hover:bg-white hover:text-navy font-head font-bold h-14 px-8 text-base">
                <Phone className="w-5 h-5 mr-1" /> {PHONE}
              </Button>
            </a>
          </div>
          <div className="mt-8 flex items-center gap-4 text-slate-400 text-sm">
            <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-orange-400 text-orange-400" />)}</div>
            <span className="font-head font-semibold text-white">5.0</span> Based on 100+ Google Reviews
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="relative">
          <div className="relative border-4 border-white/10">
            <img src="https://images.unsplash.com/photo-1656122381069-9ec666d95cf1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
              alt="Freshly painted interior" className="w-full h-[300px] sm:h-[440px] object-cover" data-testid="hero-image" />
            <div className="absolute -bottom-5 -left-5 bg-brand-orange text-white p-5 max-w-[220px] shadow-xl hidden sm:block">
              <p className="font-head font-black text-3xl leading-none">12+</p>
              <p className="text-sm mt-1 font-medium">Years of trusted painting experience</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export function ReviewsBlock() {
  return (
    <section className="py-20 md:py-28 bg-slate-50" data-testid="reviews-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionTitle overline="Testimonials" title="What Our Customers Say" />
        <div className="mt-6 flex items-center justify-center gap-3">
          <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} className="w-6 h-6 fill-orange-400 text-orange-400" />)}</div>
          <span className="font-head font-black text-2xl text-navy">5.0</span>
          <span className="text-slate-500 text-sm">· 100+ Google Reviews</span>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reviews.map((r, i) => (
            <motion.div key={r.name} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
              className="bg-white border border-slate-200 p-8 flex flex-col" data-testid={`review-card-${i}`}>
              <div className="flex">{[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-orange-400 text-orange-400" />)}</div>
              <blockquote className="mt-4 text-slate-700 leading-relaxed italic flex-1">"{r.text}"</blockquote>
              <p className="mt-5 font-head font-bold text-brand-orange text-sm">— {r.name}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function AreasBlock() {
  return (
    <section className="py-20 md:py-28" data-testid="areas-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionTitle overline="Where We Work" title="Proudly Serving Aurora & The GTA" />
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {areaDetails.map((a, i) => (
            <motion.div key={a.name} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
              className="border border-slate-200 p-8 flex items-start gap-4 hover:border-orange-300 transition-colors" data-testid={`area-card-${i}`}>
              <div className="w-12 h-12 bg-navy flex items-center justify-center shrink-0"><MapPin className="w-6 h-6 text-brand-orange" /></div>
              <div>
                <h3 className="font-head font-bold text-navy text-lg">{a.name}</h3>
                <p className="mt-1 text-slate-500 text-sm">{a.note}</p>
              </div>
            </motion.div>
          ))}
        </div>
        <p className="mt-8 text-center text-slate-500">...and all surrounding Greater Toronto Area communities.</p>
      </div>
    </section>
  );
}
